/**
 * @file secondary-tools-handler.ts
 * @description Coordinates auxiliary canvas interactions like color picking (Eyedropper), region selection (Magic Wand), and manual zoom controls.
 */

import { g, Tool } from '@hcie/core';
import { eyeDropperColor, magicWandSelection } from '@hcie/tools';
import { getActiveLayer } from '../layer-service';
import { renderLayers } from '../render-engine';

export class SecondaryToolsHandler {
    /**
     * @function handleMouseDown
     * @description Routes mouse-down events to secondary tool implementations.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if the operation was handled by a secondary tool.
     */
    public static handleMouseDown(event: MouseEvent): boolean {
        // [CRITICAL_LOGIC_START]
        const toolId: string = g.current_tool.id;

        switch (toolId) {
            case Tool.Eye_Dropper.id:
                this.executeEyedropper();
                return true;

            case Tool.Wand.id:
                this.executeMagicWand(event);
                return true;

            case Tool.Zoom_In.id:
                this.applyManualZoom(true);
                return true;

            case Tool.Zoom_Out.id:
                this.applyManualZoom(false);
                return true;

            default:
                return false;
        }
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function executeEyedropper
     * @description Samples a pixel color from the primary drawing canvas at the current mouse position.
     * @private
     */
    private static executeEyedropper(): void {
        const drawingCanvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
        const canvasContext = drawingCanvas?.getContext("2d");
        
        if (!canvasContext) {
            return;
        }

        // [CRITICAL_LOGIC_START]
        // Use floor to ensure we sample exactly over the pixel the mouse is pointing at.
        const targetX: number = Math.floor(g.pX);
        const targetY: number = Math.floor(g.pY);
        
        const sampledColor: string | null = eyeDropperColor(targetX, targetY, canvasContext);
        
        if (sampledColor) {
            g.pen_color = sampledColor;
            
            // Dispatch shared event for UI synchronization (swatches, palettes, etc.)
            window.dispatchEvent(new CustomEvent('colorChanged', { 
                detail: { color: g.pen_color, source: 'eyedropper' } 
            }));
            
            // Invoke legacy UI update bridge if present
            if (typeof (window as any).updateOptionsBar === 'function') {
                (window as any).updateOptionsBar();
            }
        }
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function executeMagicWand
     * @description Triggers a contiguous color-based selection on the active layer.
     * @param {MouseEvent} event - The mouse event to determine selection mode (add/sub).
     * @private
     */
    private static executeMagicWand(event: MouseEvent): void {
        const activeLayer = getActiveLayer();
        if (!activeLayer || !activeLayer.ctx) {
            return;
        }

        // [CRITICAL_LOGIC_START]
        const tolerance: number = g.fill_tolerance || 30;
        const selectionMode: string = event.shiftKey ? "add" : (event.altKey ? "subtract" : "replace");

        magicWandSelection(
            Math.floor(g.pX), 
            Math.floor(g.pY), 
            activeLayer.ctx as CanvasRenderingContext2D, 
            tolerance, 
            selectionMode as any
        );
        
        renderLayers();
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function applyManualZoom
     * @description Increments or decrements the global canvas zoom level.
     * @private
     */
    private static applyManualZoom(isZoomIn: boolean): void {
        const zoomDelta: number = g.zoomFactor || 1.2;
        
        if (isZoomIn) {
            g.zoom *= zoomDelta;
        } else {
            g.zoom /= zoomDelta;
        }
        
        renderLayers();
    }
}
